import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, CheckCircle2, AlertTriangle, Truck, ClipboardCheck } from "lucide-react";
import { seoHead, serviceSchema } from "@/lib/seo";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { useBusinessConfig } from "@/lib/use-business-config";

const FAQS = [
  {
    q: "What is a Class 7 MOT?",
    a: "A Class 7 MOT covers goods vehicles with a design gross weight between 3,000kg and 3,500kg — typically larger vans such as Mercedes Sprinters, Ford Transits (L3/L4), VW Crafters, Iveco Dailys and similar light commercials.",
  },
  {
    q: "How much does a Class 7 MOT cost in London?",
    a: `The DVSA maximum fee for a Class 7 MOT is £58.60. At Woodseer Autos we charge the standard DVSA rate — call ${NAP.phone} for current pricing and same-week slots.`,
  },
  {
    q: "Are you a DVSA-authorised Class 7 MOT station?",
    a: "Yes. Woodseer Autos is a DVSA-authorised MOT testing station in Poplar, E14, approved for Classes 1, 2, 4, 5 and 7. Vehicles enter via 14 Copenhagen Place, E14 7DF.",
  },
  {
    q: "What are the most common Class 7 MOT failure points?",
    a: "Headlight aim and bulbs, tyre tread on heavily-loaded rear axles, brake imbalance, corroded brake pipes under the load floor, side-door latches, rear-door catches, suspension bushes, and exhaust corrosion are the biggest failure categories on London vans.",
  },
  {
    q: "Do you offer Class 7 servicing as well?",
    a: "Yes — we service Sprinters, Transits, Crafters and similar light commercials. Bring it in with the MOT and we'll quote any work needed in plain English before touching it.",
  },
  {
    q: "How long does a Class 7 MOT take?",
    a: "Typically 60–90 minutes, slightly longer than a Class 4. Drop-off and collection are welcome — leave the keys, we'll call before doing any work.",
  },
];

const FAILURE_POINTS = [
  { title: "Headlight aim & bulbs", body: "Heavy rear loads tilt vans and throw beam aim out. We re-aim on the beam tester as part of the test." },
  { title: "Brake imbalance", body: "Side-to-side brake imbalance > 30% is an automatic fail. Common on vans driven mostly empty." },
  { title: "Corroded brake pipes", body: "Salt and damp underbody cause pipe corrosion under load floors — check before the test, not after." },
  { title: "Tyre tread on rear axle", body: "Loaded rear axles wear tread faster than the front. 1.6mm is the legal minimum across the central 3/4." },
  { title: "Side & rear door latches", body: "Worn striker plates and slam-locks routinely fail on Sprinters, Transits and Crafters." },
  { title: "Suspension bushes", body: "Rear leaf-spring bushes and front wishbone bushes perish under constant loading." },
];

export const Route = createFileRoute("/guide/class-7-mot")({
  head: () =>
    seoHead({
      path: "/guide/class-7-mot",
      title: "Class 7 MOT London — Requirements & Costs | Woodseer E14",
      description:
        `Class 7 MOT testing in East London for vans up to 3,500kg. DVSA-authorised station in Poplar E14. Pricing, requirements, common failures and how to book. Call ${NAP.phone}.`,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Motorist Help", path: "/motorist-help" },
        { name: "Class 7 MOT London", path: "/guide/class-7-mot" },
      ],
      jsonLd: [
        serviceSchema({
          name: "Class 7 MOT Testing",
          serviceType: "Class 7 MOT (goods vehicles up to 3,500kg)",
          description:
            "DVSA-authorised Class 7 MOT testing in Poplar, East London for vans and light commercial vehicles between 3,000kg and 3,500kg design gross weight.",
          areaServed: ["East London", "Poplar", "Canary Wharf", "Tower Hamlets", "E14"],
          url: "https://woodseerautos.co.uk/guide/class-7-mot",
          offers: { price: "58.60", priceCurrency: "GBP", description: "DVSA maximum Class 7 MOT fee" },
        }),
        faqSchema(FAQS) as Record<string, unknown>,
      ],
    }),
  component: ClassSevenGuide,
});

function ClassSevenGuide() {
  const biz = useBusinessConfig();
  return (
    <>
      <section className="bg-foreground text-background py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Van & light commercial</p>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
            Class 7 MOT in East London — requirements & costs
          </h1>
          <p className="mt-5 text-base md:text-lg text-background/70 leading-relaxed max-w-2xl">
            Woodseer Autos is a DVSA-authorised Class 7 MOT station in Poplar, E14. If you run a van between 3,000kg and 3,500kg —
            Sprinter, Transit, Crafter, Daily — this is what you need to know before booking.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={biz.phoneHref}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <Phone className="h-4 w-4" /> Call {biz.phone}
            </a>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-md border border-background/30 px-6 py-3 text-sm font-semibold text-background hover:bg-background/10"
            >
              Book a Class 7 MOT slot
            </Link>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-6 grid md:grid-cols-3 gap-5">
          {[
            { icon: Truck, title: "Eligible vehicles", body: "Vans with design gross weight 3,000–3,500kg. Sprinter, Transit L3/L4, Crafter, Daily, Master, Boxer." },
            { icon: ClipboardCheck, title: "DVSA fee", body: "£58.60 maximum. We charge the standard DVSA rate — no inflated van premium." },
            { icon: CheckCircle2, title: "Test duration", body: "60–90 minutes typically. Drop-off welcome, we call before any work." },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                <b.icon className="h-5 w-5" />
              </div>
              <h2 className="mt-4 text-lg font-bold text-foreground">{b.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-14 md:py-20 bg-secondary/20 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <div className="flex items-center gap-2 text-primary">
            <AlertTriangle className="h-5 w-5" />
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase">Common failure points</p>
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">
            What we see fail Class 7 MOTs in London
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {FAILURE_POINTS.map((p) => (
              <div key={p.title} className="rounded-xl border border-border bg-card p-5">
                <h3 className="text-base font-bold text-foreground">{p.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <FAQAccordion faqs={FAQS} title="Class 7 MOT — frequently asked questions" />
        </div>
      </section>

      <CTABlock
        headline="Need a Class 7 MOT slot this week?"
        subtext="DVSA-authorised station in Poplar, E14. Honest advice, fair pricing, same-week availability for vans."
      />
    </>
  );
}
